<template>
  <div>
    <div ref="mapEl" class="ridelilo-map"></div>
    <v-btn
      v-if="markers && markers.length > 1"
      depressed
      color="success"
      @click="fitBounds"
      class="ma-1 float-right my-2"
    >
      Fit
      <v-icon right dark>mdi-fit-to-screen-outline</v-icon>
    </v-btn>
  </div>
</template>

<script>
import maplibregl from "maplibre-gl";
import axios from "axios";

const STYLE = "https://tiles.openfreemap.org/styles/liberty";

function toGooglePlace(payload, fallbackLat, fallbackLng) {
  const loc = (payload && payload.geometry && payload.geometry.location) || {};
  const lat = Number(loc.lat != null ? loc.lat : fallbackLat);
  const lng = Number(loc.lng != null ? loc.lng : fallbackLng);
  return {
    place_id: (payload && payload.place_id) || `coord-${lat}-${lng}`,
    formatted_address:
      (payload && (payload.formatted_address || payload.description)) ||
      `${lat.toFixed(5)}, ${lng.toFixed(5)}`,
    geometry: {
      location: {
        lat: function () {
          return lat;
        },
        lng: function () {
          return lng;
        },
      },
    },
  };
}

export default {
  props: {
    center: Object,
    zoom: Number,
    apiKey: String,
    markers: Array,
    selected: String,
    enabled: Boolean,
    polylines: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      map: null,
      markerObjs: [],
      popup: null,
    };
  },
  watch: {
    markers: {
      handler() {
        this.syncMarkers();
        this.$nextTick(() => this.fitBounds());
      },
      deep: true,
    },
    polylines: {
      handler() {
        this.syncPolylines();
      },
      deep: true,
    },
    center: {
      handler(val) {
        if (this.map && val && val.lat != null && val.lng != null) {
          this.map.setCenter([Number(val.lng), Number(val.lat)]);
        }
      },
      deep: true,
    },
    selected() {
      this.showSelectedPopup();
    },
  },
  mounted() {
    const center = this.center || { lat: 9.005401, lng: 38.763611 };
    this.map = new maplibregl.Map({
      container: this.$refs.mapEl,
      style: STYLE,
      center: [Number(center.lng), Number(center.lat)],
      zoom: this.zoom || 12,
      attributionControl: true,
    });
    this.map.addControl(new maplibregl.NavigationControl(), "top-right");
    this.map.on("load", () => {
      this.syncPolylines();
      this.syncMarkers();
      this.fitBounds();
      this.map.resize();
    });
    this.map.on("click", (e) => this.onMapClick(e));
    this._onResize = () => {
      if (this.map) this.map.resize();
    };
    window.addEventListener("resize", this._onResize);
  },
  beforeDestroy() {
    if (this._onResize) {
      window.removeEventListener("resize", this._onResize);
    }
    this.clearMarkers();
    if (this.popup) {
      this.popup.remove();
      this.popup = null;
    }
    if (this.map) {
      this.map.remove();
      this.map = null;
    }
  },
  methods: {
    onMapClick(event) {
      if (!this.enabled) return;
      const lat = event.lngLat.lat;
      const lng = event.lngLat.lng;
      axios
        .get("/maps/geocode", { params: { lat, lng } })
        .then((res) => {
          this.$emit("map-click", toGooglePlace(res.data, lat, lng));
        })
        .catch(() => {
          this.$emit("map-click", toGooglePlace(null, lat, lng));
        });
    },
    clearMarkers() {
      this.markerObjs.forEach((m) => m.remove());
      this.markerObjs = [];
    },
    syncMarkers() {
      if (!this.map) return;
      this.clearMarkers();
      (this.markers || []).forEach((m) => {
        if (!m || !m.position) return;
        const lng = Number(m.position.lng);
        const lat = Number(m.position.lat);
        if (Number.isNaN(lat) || Number.isNaN(lng)) return;
        const el = document.createElement("div");
        if (m.icon) {
          const img = document.createElement("img");
          img.src = typeof m.icon === "string" ? m.icon : m.icon.url || m.icon;
          img.width = 32;
          img.height = 32;
          el.appendChild(img);
        } else {
          el.className = "ridelilo-pin";
        }
        const marker = new maplibregl.Marker({ element: el })
          .setLngLat([lng, lat])
          .addTo(this.map);
        if (m.infoText) {
          marker.setPopup(new maplibregl.Popup({ offset: 25 }).setHTML(m.infoText));
        }
        marker._placeId = m.place_id;
        this.markerObjs.push(marker);
      });
      this.showSelectedPopup();
    },
    syncPolylines() {
      if (!this.map || !this.map.getStyle()) return;
      const features = [];
      (this.polylines || []).forEach((p, index) => {
        if (!p || !p.data || !p.data.length) return;
        features.push({
          type: "Feature",
          properties: { color: p.strokeColor || "#1A73E8" },
          geometry: {
            type: "LineString",
            coordinates: p.data.map((pt) => [Number(pt.lng), Number(pt.lat)]),
          },
        });
      });
      const geojson = { type: "FeatureCollection", features };
      if (this.map.getSource("ridelilo-lines")) {
        this.map.getSource("ridelilo-lines").setData(geojson);
        return;
      }
      this.map.addSource("ridelilo-lines", { type: "geojson", data: geojson });
      this.map.addLayer({
        id: "ridelilo-lines-layer",
        type: "line",
        source: "ridelilo-lines",
        paint: {
          "line-color": ["get", "color"],
          "line-width": 5,
        },
      });
    },
    fitBounds() {
      if (!this.map || !this.markers || this.markers.length < 2) return;
      const bounds = new maplibregl.LngLatBounds();
      let count = 0;
      this.markers.forEach((m) => {
        if (!m || !m.position) return;
        bounds.extend([Number(m.position.lng), Number(m.position.lat)]);
        count += 1;
      });
      if (count < 2) return;
      this.map.fitBounds(bounds, { padding: 48, maxZoom: 16 });
    },
    showSelectedPopup() {
      if (!this.selected) return;
      const marker = this.markerObjs.find((m) => m._placeId == this.selected);
      if (marker) {
        const popup = marker.getPopup();
        if (popup) marker.togglePopup();
      }
    },
  },
};
</script>

<style>
.ridelilo-map {
  width: 100%;
  height: 400px;
  border-radius: 4px;
}
.ridelilo-pin {
  width: 18px;
  height: 18px;
  background: #c62828;
  border: 2px solid #fff;
  border-radius: 50%;
}
</style>
